/**
 * Sistema de Party (grupos entre jugadores) — constantes, tipos y helpers puros
 * compartidos entre server y cliente. La LÓGICA vive en el server
 * (`server/src/systems/PartyManager.ts`); acá sólo el contrato de datos + reglas
 * puras (bono de diversidad, rango de reparto, validación de shares de XP).
 *
 * Ver docs/PARTY_SYSTEM.md.
 */
import { VIEW_TILES_X, VIEW_TILES_Y } from '../constants';
import {
  type CharacterClassId,
  isNonMagicClass,
} from '../types/character';

// =============================================================================
// CONSTANTES
// =============================================================================

/** Tamaño máximo de un party (líder incluido). */
export const PARTY_MAX_SIZE = 6;

/** Diferencia máxima de nivel permitida entre CUALQUIER par de miembros. */
export const PARTY_MAX_LEVEL_GAP = 5;

/**
 * Gracia (ms) tras romper la banda de nivel (por un level-up) antes de expulsar
 * automáticamente al miembro que quedó fuera de rango. Ver docs/PARTY_SYSTEM.md.
 */
export const PARTY_LEVEL_GAP_GRACE_MS = 300_000; // 5 min

/**
 * Rango (half, en tiles, métrica Chebyshev) dentro del cual un miembro es
 * elegible para recibir el reparto por kill. Objetivo: ~1.5 pantallas. La
 * pantalla es VIEW_TILES_X × VIEW_TILES_Y (38×25); 1.5 pantallas ≈ 57×37 tiles,
 * y el half-range es la mitad de eso. Se usa como |dx| ≤ X y |dy| ≤ Y desde el
 * tile del NPC muerto.
 */
export const PARTY_SHARE_RANGE_X = Math.ceil((VIEW_TILES_X * 1.5) / 2); // 29
export const PARTY_SHARE_RANGE_Y = Math.ceil((VIEW_TILES_Y * 1.5) / 2); // 19

/** Color del nombre de los miembros del propio party (nameplate + roster). */
export const PARTY_NAME_COLOR = '#3fc7ff';

/** Máximo % de XP que puede recibir un solo miembro con reparto custom. */
export const PARTY_XP_SHARE_MAX = 75;
/** La suma de todos los % de reparto custom debe dar exactamente esto. */
export const PARTY_XP_SHARE_TOTAL = 100;

// =============================================================================
// POLÍTICAS DE REPARTO
// =============================================================================

/** Cómo se reparte un recurso "unitario" (oro / un item) entre los elegibles. */
export type PartyDistributionPolicy = 'even' | 'random' | 'leader' | 'round_robin';

/** Cómo se asigna el crédito de un kill de quest a UN miembro. */
export type PartyQuestPolicy = 'order' | 'round_robin' | 'leader';

/** Visibilidad del party en el Party Finder. */
export type PartyVisibility = 'public' | 'closed';

export const DEFAULT_GOLD_POLICY: PartyDistributionPolicy = 'round_robin';
export const DEFAULT_LOOT_POLICY: PartyDistributionPolicy = 'round_robin';
export const DEFAULT_QUEST_POLICY: PartyQuestPolicy = 'round_robin';

// =============================================================================
// VIEWS (contrato client ↔ server, viajan por PARTY_SYNC / PARTY_FINDER_LIST)
// =============================================================================

/** Un miembro tal como el cliente lo pinta en el roster (cara + nombre + clase). */
export interface PartyMemberView {
  sessionId: string;
  name: string;
  classId: CharacterClassId;
  level: number;
  /** '' = sin facción (Newbie). */
  faction: string;
  // Apariencia para el retrato (cara a la izquierda).
  headId: number;
  bodyId: number;
  nakedBodyId: number;
  sex: string;
  race: string;
  // Vida para la barra del roster.
  currentHp: number;
  maxHp: number;
  /** Online/conectado (un miembro puede quedar offline sin disolver el party). */
  online: boolean;
  /** Posición en el orden del party (0 = primero → prioridad de crédito de quest). */
  order: number;
  isLeader: boolean;
  /** Porta la habilidad de control de XP (amuleto/keystone). */
  hasXpControl: boolean;
}

/** Aviso de banda de nivel rota: `sessionId` será expulsado al llegar `expiresAt`. */
export interface PartyLevelGapWarning {
  sessionId: string;
  /** Epoch ms en que expira la gracia. */
  expiresAt: number;
}

/** Estado completo del party del jugador (PARTY_SYNC). */
export interface PartyView {
  id: string;
  leaderSessionId: string;
  members: PartyMemberView[];
  goldPolicy: PartyDistributionPolicy;
  lootPolicy: PartyDistributionPolicy;
  questPolicy: PartyQuestPolicy;
  visibility: PartyVisibility;
  /** Nivel sugerido mostrado en el Party Finder (sólo si es público). */
  minLevelTag?: number;
  /**
   * Reparto de XP custom por sessionId (porcentaje entero). null/ausente = parejo.
   */
  xpShares?: Record<string, number> | null;
  /** El jugador local puede editar el reparto de XP (porta la habilidad). */
  canEditXpShares?: boolean;
  /** Aviso activo de banda de nivel rota (para la UI). */
  levelGapWarning?: PartyLevelGapWarning | null;
}

/** Resumen de un party público para la lista del Party Finder. */
export interface PartyFinderEntry {
  id: string;
  leaderName: string;
  memberCount: number;
  maxSize: number;
  minLevelTag?: number;
  members: PartyMemberView[];
}

// =============================================================================
// HELPERS PUROS
// =============================================================================

/**
 * Bono de XP por diversidad de clase (multiplicador decimal a sumar a 1).
 * Se basa en la cantidad de CLASES DISTINTAS entre los miembros elegibles:
 *   3 → +0.05, 4 → +0.10, 5 → +0.15, 6 → +0.20.
 * Los tiers 5 y 6 exigen además ≥2 clases SIN maná en el grupo; si no se cumple,
 * el bono cae al tier inferior cuya condición sí se cumpla.
 */
export function getClassDiversityBonus(classIds: readonly CharacterClassId[]): number {
  const distinct = new Set(classIds);
  const distinctCount = distinct.size;
  let nonMagicCount = 0;
  for (const c of distinct) {
    if (isNonMagicClass(c)) nonMagicCount++;
  }

  // Tier alcanzable por cantidad de clases distintas.
  let tier = Math.min(distinctCount, PARTY_MAX_SIZE);
  // Los tiers 5/6 requieren ≥2 clases sin maná: si no, bajar hasta 4.
  if (tier >= 5 && nonMagicCount < 2) tier = 4;

  switch (tier) {
    case 6:
      return 0.2;
    case 5:
      return 0.15;
    case 4:
      return 0.1;
    case 3:
      return 0.05;
    default:
      return 0;
  }
}

/** True si dos tiles están dentro del rango de reparto (Chebyshev por eje). */
export function isWithinShareRange(
  ax: number,
  ay: number,
  bx: number,
  by: number
): boolean {
  return Math.abs(ax - bx) <= PARTY_SHARE_RANGE_X && Math.abs(ay - by) <= PARTY_SHARE_RANGE_Y;
}

/**
 * Valida un mapa de reparto custom de XP contra un set de sessionIds válidos
 * (los miembros actuales del party). Reglas: mismas claves que los miembros,
 * cada valor entero 0..PARTY_XP_SHARE_MAX, suma == PARTY_XP_SHARE_TOTAL.
 * Devuelve un motivo de error o null si es válido.
 */
export function validateXpShares(
  shares: Record<string, number>,
  memberSessionIds: readonly string[]
): string | null {
  const members = new Set(memberSessionIds);
  const keys = Object.keys(shares);
  if (keys.length !== members.size) return 'Faltan o sobran miembros en el reparto.';
  let sum = 0;
  for (const k of keys) {
    if (!members.has(k)) return 'El reparto incluye a alguien que no está en el party.';
    const v = shares[k];
    if (!Number.isInteger(v) || v < 0 || v > PARTY_XP_SHARE_MAX) {
      return `Cada porcentaje debe ser un entero entre 0 y ${PARTY_XP_SHARE_MAX}.`;
    }
    sum += v;
  }
  if (sum !== PARTY_XP_SHARE_TOTAL) return `Los porcentajes deben sumar exactamente ${PARTY_XP_SHARE_TOTAL}.`;
  return null;
}
